/**
 * Pure helpers for World Cup Picks: stage-ordered sections and FlashList flattening.
 */

import { format, parseISO } from 'date-fns';
import { LEAGUE_DATE_HEADER_FORMAT } from '../../gameResults/constants/gameResults.constants';
import type { TournamentStageKey, WorldCupMatch } from '../types/worldcup.types';
import { GROUP_STAGE_FRONTEND_KEYS } from './worldcupPicksData.helper';

export const WORLD_CUP_PICKS_STAGE_ORDER: TournamentStageKey[] = [
  'first_round',
  'second_round',
  'third_round',
  'round_of_32',
  'round_of_16',
  'quarter_finals',
  'semi_finals',
  'third_place',
  'final',
];

export type PicksFlashListSort = 'asc' | 'desc';

export interface WorldCupPicksStageSection {
  key: TournamentStageKey;
  label: string;
  matches: WorldCupMatch[];
}

export type PicksFlashListItem =
  | { type: 'header'; stageKey: TournamentStageKey; label: string }
  | { type: 'dateHeader'; stageKey: TournamentStageKey; dateKey: string; title: string }
  | { type: 'match'; stageKey: TournamentStageKey; match: WorldCupMatch };

/** YYYY-MM-DD from ISO kickoff (same bucketing as fixtures-by-league date headers). */
export function kickoffCalendarDateKey(kickoffIso: string): string {
  return kickoffIso.slice(0, 10);
}

/**
 * Splits already kickoff-sorted matches into consecutive calendar-day runs (preserves list order).
 */
export function chunkMatchesByKickoffDate(matches: WorldCupMatch[]): { dateKey: string; matches: WorldCupMatch[] }[] {
  const chunks: { dateKey: string; matches: WorldCupMatch[] }[] = [];
  for (const match of matches) {
    const dateKey = kickoffCalendarDateKey(match.kickoffTime);
    const last = chunks[chunks.length - 1];
    if (last && last.dateKey === dateKey) {
      last.matches.push(match);
    } else {
      chunks.push({ dateKey, matches: [match] });
    }
  }
  return chunks;
}

function compareKickoff(a: WorldCupMatch, b: WorldCupMatch, sort: PicksFlashListSort): number {
  const ta = new Date(a.kickoffTime).getTime();
  const tb = new Date(b.kickoffTime).getTime();
  return sort === 'asc' ? ta - tb : tb - ta;
}

/**
 * Groups matches by tournament stage (fixed order), sorts within each stage by kickoff.
 */
export function buildSortedStageSections(
  matches: WorldCupMatch[],
  sort: PicksFlashListSort,
  stageOrder: TournamentStageKey[] = WORLD_CUP_PICKS_STAGE_ORDER,
): WorldCupPicksStageSection[] {
  return stageOrder
    .map((stageKey) => {
      const stageMatches = matches.filter((m) => m.stageKey === stageKey);
      if (stageMatches.length === 0) return null;
      const sorted = [...stageMatches].sort((a, b) => compareKickoff(a, b, sort));
      return {
        key: stageKey,
        label: sorted[0].stageLabel,
        matches: sorted,
      };
    })
    .filter((section): section is WorldCupPicksStageSection => section !== null);
}

export interface FlattenPicksFlashListResult {
  data: PicksFlashListItem[];
  stickyHeaderIndices: number[];
}

/**
 * One FlashList row per stage header, date header (fixtures-by-league style), or match.
 * Sticky indices: stage headers and date headers.
 */
export function flattenPicksStageSectionsForFlashList(
  sections: WorldCupPicksStageSection[],
): FlattenPicksFlashListResult {
  const data: PicksFlashListItem[] = [];
  const stickyHeaderIndices: number[] = [];

  for (const section of sections) {
    if (!GROUP_STAGE_FRONTEND_KEYS.includes(section.key)) {
      stickyHeaderIndices.push(data.length);
      data.push({ type: 'header', stageKey: section.key, label: section.label });
    }
    for (const chunk of chunkMatchesByKickoffDate(section.matches)) {
      stickyHeaderIndices.push(data.length);
      data.push({
        type: 'dateHeader',
        stageKey: section.key,
        dateKey: chunk.dateKey,
        title: format(parseISO(chunk.dateKey), LEAGUE_DATE_HEADER_FORMAT),
      });
      for (const match of chunk.matches) {
        data.push({ type: 'match', stageKey: section.key, match });
      }
    }
  }

  return { data, stickyHeaderIndices };
}
